import { useState, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import {
  Folder,
  Edit3,
  Trash2,
  Share2,
  // MoreHorizontal,
  // Users,
  // Star,
} from 'lucide-react';
import { cn, truncateText } from '@/lib/utils';
import { navigateTo } from '@/lib/hashRouter';
import { Conversation, Project } from '@/types';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import DeleteConversationDialog from '@/components/DeleteConversationDialog';
import ProjectDropdown from './ProjectDropdown';
import RenameDialog from './RenameDialog';
interface ProjectListProps {
  projects: Project[];
  currentProjectId?: string | null;
  currentConversationId?: string;
  shareConversationId?: string;
  hasLoadingConversations: boolean;
  isHighlighted?: boolean; // Controls whether this list should show highlighted state
  onProjectSelect: (projectId: string) => void;
  onConversationSelect: (id: string, projectId: string) => void;
  onRenameProject: (projectId: string, newName: string) => void;
  onUnstarProject?: (projectId: string) => void; // 🔥 取消收藏项目
  onRenameConversation: (id: string, title: string) => void;
  onDeleteConversation: (id: string) => Promise<void>;
  onShareConversation: (conversation: Conversation) => void;
  onBackToChat: () => void;
  onBeforeAction?: () => void; // 在执行任何操作前调用（关闭 User Settings 等）
  hideConversations?: boolean; // 🔥 收藏项目不渲染 conversations 列表
}

// 🔥 优化：使用 memo 避免频繁重新渲染project列表
// ProjectList内部使用useConversationsContext获取数据，所以不需要在props中传递
const ProjectList = memo(function ProjectList({
  projects,
  currentProjectId,
  currentConversationId,
  shareConversationId,
  // hasLoadingConversations, // 🔥 不再使用，改为检查 conversation.isLoading
  isHighlighted = false,
  onProjectSelect,
  onConversationSelect,
  onRenameProject,
  onUnstarProject,
  onRenameConversation,
  onDeleteConversation,
  onShareConversation,
  onBackToChat,
  onBeforeAction,
  hideConversations = false,
}: ProjectListProps) {
  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);

  // 重命名对话框状态 - 项目重命名
  const [projectRenameDialog, setProjectRenameDialog] = useState<{
    isOpen: boolean;
    projectId: string | null;
    projectName: string;
  }>({ isOpen: false, projectId: null, projectName: '' });

  // 重命名对话框状态 - 会话重命名
  const [conversationRenameDialog, setConversationRenameDialog] = useState<{
    isOpen: boolean;
    conversationId: string | null;
    conversationTitle: string;
  }>({ isOpen: false, conversationId: null, conversationTitle: '' });

  // 监听语言变化
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);
  // Get global conversation state
  const { expandedProjectIds, sidebarProjectConversationsCache } =
    useConversationsContext();
  const [conversationConfirmDialog, setConversationConfirmDialog] = useState<{
    isOpen: boolean;
    conversationId?: string;
    conversationTitle?: string;
  }>({ isOpen: false });
  // 打开项目重命名对话框
  const handleRenameProject = (project: Project) => {
    onBeforeAction?.(); // 先关闭 User Settings
    setProjectRenameDialog({
      isOpen: true,
      projectId: project.id,
      projectName: project.name,
    });
  };

  // 打开会话重命名对话框
  const handleRenameConversation = (conversation: Conversation) => {
    if (conversation.isLoading) return;
    onBeforeAction?.(); // 先关闭 User Settings
    setConversationRenameDialog({
      isOpen: true,
      conversationId: conversation.id,
      conversationTitle: conversation.title,
    });
  };
  if (projects.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-xs text-gray-500">{t('sidebar.noProjects')}</p>
      </div>
    );
  }
  return (
    <>
      {projects.map(project => {
        // const isExpanded = expandedProjectIds.has(project.id);
        // const conversations =
        //   sidebarProjectConversationsCache[project.id];
        // const hasConversations = conversations && conversations.length > 0;
        // const shouldShowConversations = isExpanded && hasConversations;
        return (
          <div
            key={project.id}
            className="relative mb-1 rounded-lg transition-colors"
          >
            <div
              className={cn(
                'group relative flex gap-2 px-2 py-2 rounded-lg cursor-pointer',
                // Project 只在以下情况高亮：
                // 1. 当前 project 是这个 project
                // 2. 且没有选中任何对话（即在 project-detail 视图）
                // 3. 且 isHighlighted 为 true（用于区分 Starred 和 Projects 区域）
                isHighlighted &&
                  currentProjectId === project.id &&
                  !currentConversationId
                  ? 'bg-[#e8e6dc] border border-gray-200 shadow-sm'
                  : 'hover:bg-[#e8e6dc]'
              )}
              onClick={() => {
                // Switch to project detail view
                onProjectSelect(project.id);
              }}
            >
              <Folder size={16} className="text-gray-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {truncateText(project.name, 30)}
                </div>
              </div>
              {!shareConversationId && (
                <div
                  className="absolute right-2 hidden items-center gap-1 group-hover:flex transition-all bg-[#e8e6dc] rounded-md px-1"
                  style={{ top: '50%', marginTop: '-12px' }}
                >
                  <ProjectDropdown
                    project={project}
                    onRename={() => handleRenameProject(project)}
                    onMembers={() => {
                      onBeforeAction?.(); // 先关闭 User Settings
                      navigateTo({
                        type: 'project-members',
                        projectId: project.id,
                      });
                    }}
                    onUnstar={
                      onUnstarProject
                        ? () => {
                            onBeforeAction?.(); // 先关闭 User Settings
                            onUnstarProject(project.id);
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
            {/* Project Conversations - 🔥 收藏项目不渲染 conversations 列表 */}
            {!hideConversations &&
              expandedProjectIds.has(project.id) &&
              sidebarProjectConversationsCache[project.id]?.length > 0 && (
                <div className="mt-2">
                  {(sidebarProjectConversationsCache[project.id] || []).map(
                    conversation => (
                      <div
                        key={conversation.id}
                        className={cn(
                          'group relative flex items-start gap-3 px-3 py-1 mb-1 rounded-lg cursor-pointer transition-colors',
                          currentConversationId === conversation.id
                            ? 'bg-[#e8e6dc] border border-gray-200 shadow-sm'
                            : 'hover:bg-[#e8e6dc]'
                        )}
                        onClick={() => {
                          onBackToChat();
                          onConversationSelect(conversation.id, project.id);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div>
                            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                              <div className="text-sm font-medium text-gray-800 truncate leading-relaxed">
                                {truncateText(conversation.title, 35)}
                              </div>
                              {conversation.isLoading && (
                                <div
                                  className="animate-spin rounded-full h-3 w-3 border-b border-gray-400 flex-shrink-0"
                                  title={t('common.loading')}
                                ></div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* 🔥 修复：只有当前 loading 的 conversation 禁止操作，其他 conversation 不受影响 */}
                        {!shareConversationId && !conversation.isLoading && (
                          <div
                            className="absolute right-2 hidden items-center gap-1 group-hover:flex transition-all bg-[#e8e6dc] rounded-md px-1"
                            style={{ top: '50%', marginTop: '-12px' }}
                          >
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                handleRenameConversation(conversation);
                              }}
                              className="p-1.5 rounded text-gray-500 transition-colors hover:bg-[#e8e6dc] hover:text-gray-700"
                            >
                              <Edit3 size={12} />
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                onBeforeAction?.(); // 先关闭 User Settings
                                setConversationConfirmDialog({
                                  isOpen: true,
                                  conversationId: conversation.id,
                                  conversationTitle: conversation.title,
                                });
                              }}
                              className="p-1.5 rounded text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 size={12} />
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                onBeforeAction?.(); // 先关闭 User Settings
                                onShareConversation(conversation);
                              }}
                              className="p-1.5 rounded text-gray-500 transition-colors hover:bg-red-100 hover:text-red-600"
                            >
                              <Share2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
          </div>
        );
      })}
      {/* Conversation Delete Confirmation Dialog */}
      <DeleteConversationDialog
        isOpen={conversationConfirmDialog.isOpen}
        conversationTitle={conversationConfirmDialog.conversationTitle}
        onConfirm={async () => {
          if (conversationConfirmDialog.conversationId) {
            return onDeleteConversation(
              conversationConfirmDialog.conversationId
            );
          }
        }}
        onCancel={() => setConversationConfirmDialog({ isOpen: false })}
      />

      {/* Project Rename Dialog */}
      <RenameDialog
        isOpen={projectRenameDialog.isOpen}
        title={t('dialog.renameTitle')}
        currentName={projectRenameDialog.projectName}
        onConfirm={newName => {
          if (projectRenameDialog.projectId) {
            onRenameProject(projectRenameDialog.projectId, newName);
          }
          setProjectRenameDialog({
            isOpen: false,
            projectId: null,
            projectName: '',
          });
        }}
        onCancel={() =>
          setProjectRenameDialog({
            isOpen: false,
            projectId: null,
            projectName: '',
          })
        }
      />

      {/* Conversation Rename Dialog */}
      <RenameDialog
        isOpen={conversationRenameDialog.isOpen}
        title={t('conversation.renameTitle')}
        currentName={conversationRenameDialog.conversationTitle}
        onConfirm={newTitle => {
          if (conversationRenameDialog.conversationId) {
            onRenameConversation(
              conversationRenameDialog.conversationId,
              newTitle
            );
          }
          setConversationRenameDialog({
            isOpen: false,
            conversationId: null,
            conversationTitle: '',
          });
        }}
        onCancel={() =>
          setConversationRenameDialog({
            isOpen: false,
            conversationId: null,
            conversationTitle: '',
          })
        }
      />
    </>
  );
});

export default ProjectList;
